/**
 * Reactions on a message bubble, drawn the way iMessage draws tapbacks: a small
 * cluster of pills that overlaps the bubble's top INNER corner — top-left for a
 * bubble on the right (your own messages), top-right for a bubble on the left.
 *
 * The cluster is absolutely positioned, so the bubble (and its row) must be
 * `relative`, and the row must reserve a little top margin
 * (REACTION_ROW_CLEARANCE_CLASS) so the pills never sit on the previous message.
 */
import { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';

/** Pills shown before the rest collapse into "+N". */
export const MAX_VISIBLE_REACTIONS = 4;
/** Top padding a message row needs when its bubble carries reactions. */
export const REACTION_ROW_CLEARANCE_CLASS = 'pt-3';

export interface ReactionBadgesProps {
  /** emoji -> user ids who reacted with it */
  reactions: Record<string, number[]>;
  currentUserId: number;
  /** Which side of the thread the bubble sits on. */
  side: 'left' | 'right';
  onToggle: (emoji: string, hasReacted: boolean) => void;
  /** Resolve a user id to a display name for the tooltip (optional). */
  nameOf?: (userId: number) => string | undefined;
}

/** Position classes for the cluster: the inner top corner of the bubble. */
export function reactionCornerClasses(side: 'left' | 'right'): string {
  return side === 'right' ? '-top-3.5 -left-3 flex-row' : '-top-3.5 -right-3 flex-row-reverse';
}

export function ReactionBadges({ reactions, currentUserId, side, onToggle, nameOf }: ReactionBadgesProps) {
  const { t } = useAppTranslation();
  const [expanded, setExpanded] = useState(false);
  const entries = Object.entries(reactions).filter(([, users]) => users && users.length > 0);
  if (entries.length === 0) return null;

  const visible = expanded ? entries : entries.slice(0, MAX_VISIBLE_REACTIONS);
  const hidden = entries.length - visible.length;

  return (
    <div
      data-testid="message-reactions"
      data-side={side}
      className={cn('absolute z-[5] flex items-center gap-0.5', reactionCornerClasses(side))}
    >
      {visible.map(([emoji, users]) => {
        const mine = users.includes(currentUserId);
        const count = users.length;
        const label =
          (count === 1
            ? t('messaging.reactions.labelOne', '{{emoji}} 1 reaction', { emoji })
            : t('messaging.reactions.labelMany', '{{emoji}} {{count}} reactions', { emoji, count })) +
          (mine ? t('messaging.reactions.youReacted', ', you reacted') : '');
        const names = nameOf
          ? users.map((id) => (id === currentUserId ? t('messaging.reactions.you', 'You') : nameOf(id))).filter(Boolean)
          : [];
        const title = names.length
          ? t('messaging.reactions.reactedBy', 'Reacted by {{names}}', { names: names.join(', ') })
          : label;
        return (
          <button
            key={emoji}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(emoji, mine);
            }}
            aria-label={label}
            aria-pressed={mine}
            title={title}
            className={cn(
              'inline-flex h-6 min-w-[1.5rem] items-center justify-center gap-0.5 rounded-full border px-1.5 text-xs leading-none shadow-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
              mine
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
            )}
          >
            <span aria-hidden="true" className="text-sm leading-none">{emoji}</span>
            {count > 1 && <span aria-hidden="true" className="font-medium tabular-nums">{count}</span>}
          </button>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          aria-label={t('messaging.reactions.showMore', 'Show {{count}} more reactions', { count: hidden })}
          title={t('messaging.reactions.showMore', 'Show {{count}} more reactions', { count: hidden })}
          className="inline-flex h-6 items-center rounded-full border border-slate-200 bg-white px-1.5 text-xs font-medium leading-none text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          +{hidden}
        </button>
      )}
    </div>
  );
}
